import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageTitle, Panel, inputCls, btnPrimary } from "./ui";
import GuidesAdmin from "./GuidesAdmin";
import ToolsCatalog from "./ToolsCatalog";

const val = (v) => (Array.isArray(v) ? v.join("; ") : String(v));
const parse = (s, def) => (Array.isArray(def) ? s.split(";").map((x) => Number(x.trim().replace(",", "."))) : Number(String(s).replace(",", ".")));

function ParamRow({ p, edit, setEdit, canEdit }) {
  const e = edit[p.key] || { value: val(p.value), source: p.source, confirm: false };
  const upd = (patch) => setEdit({ ...edit, [p.key]: { ...e, ...patch } });
  return (
    <tr className="border-t border-border align-top" data-testid={`tparam-${p.key}`}>
      <td className="py-2 pr-3 text-sm"><b>{p.label}</b><span className="block text-xs text-muted-foreground">{p.key} · {p.unit}{val(p.value) !== val(p.default) ? ` · zadano ${val(p.default)}` : ""}</span></td>
      <td className="py-2 pr-3"><input disabled={!canEdit} value={e.value} onChange={(x) => upd({ value: x.target.value })} className={`${inputCls} w-36`} data-testid={`tparam-value-${p.key}`} /></td>
      <td className="py-2 pr-3"><input disabled={!canEdit} value={e.source} onChange={(x) => upd({ source: x.target.value })} className={`${inputCls} min-w-[220px]`} data-testid={`tparam-source-${p.key}`} /></td>
      <td className="py-2 text-xs">
        <span className={p.checked_at ? "" : "font-semibold text-amber-700"} data-testid={`tparam-checked-${p.key}`}>{p.checked_at ? `provjereno ${fmtDate(p.checked_at)}` : "nije potvrđeno"}</span>
        {canEdit && <label className="mt-1 flex items-center gap-1"><input type="checkbox" checked={e.confirm} onChange={(x) => upd({ confirm: x.target.checked })} data-testid={`tparam-confirm-${p.key}`} /> potvrđujem prema izvoru</label>}
      </td>
    </tr>
  );
}

export default function ToolsAdmin() {
  const { can } = useAuth();
  const canEdit = can("tools.manage");
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("catalog");
  const load = () => api.get("/admin/tools").then((r) => { setD(r.data); setEdit({}); });
  useEffect(() => { load(); }, []);
  const save = async (enabled = {}) => {
    const params = {};
    for (const [k, e] of Object.entries(edit)) {
      const p = d.params.find((x) => x.key === k);
      const value = parse(e.value, p.default);
      if ((Array.isArray(value) ? value.some(Number.isNaN) : Number.isNaN(value))) { toast.error(`Neispravna vrijednost: ${p.label}`); return; }
      if (val(value) !== val(p.value) || e.source !== p.source || e.confirm) params[k] = { value, source: e.source, confirm: e.confirm };
    }
    setBusy(true);
    try { const { data } = await api.put("/admin/tools", { enabled, params }); setD(data); setEdit({}); toast.success(data.changed.length ? `Sačuvano (${data.changed.length} izmjena)` : "Nema izmjena"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (!d) return <p className="text-sm">Učitavanje…</p>;
  return (
    <div data-testid="tools-admin">
      <PageTitle title="Alati i vodiči" sub="Kalkulatori računaju po formuli iz ovih parametara. Izmjena vrijednosti poništava potvrdu dok je ponovo ne potvrdite prema izvoru. Unosi čitalaca se ne čuvaju." />
      <nav className="mb-6 flex gap-2">{[["catalog", "Katalog i pristup"], ["tools", "Parametri"], ["guides", "Vodiči"]].map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`rounded-md px-3 py-1.5 text-sm font-semibold ${tab === k ? "bg-navy text-white" : "border border-border"}`} data-testid={`tadmin-tab-${k}`}>{l}</button>)}</nav>
      {tab === "guides" ? <GuidesAdmin /> : tab === "catalog" ? <ToolsCatalog /> : <>
      <Panel title="Kalkulatori" testId="tools-admin-list">
        <ul className="divide-y divide-border">{d.tools.map((t) => (
          <li key={t.slug} className="flex flex-wrap items-center gap-3 py-2.5 text-sm" data-testid={`tadmin-tool-${t.slug}`}>
            <span className="min-w-0 flex-1"><b>{t.title}</b> <span className="text-xs text-muted-foreground">{t.group} · {t.uses_30d} izračuna (30 dana)</span></span>
            <a href={`/alati/${t.slug}`} target="_blank" rel="noreferrer" className="text-xs underline">otvori</a>
            <label className="flex items-center gap-2"><input type="checkbox" disabled={!canEdit || busy} checked={t.enabled} onChange={(e) => save({ [t.slug]: e.target.checked })} data-testid={`tadmin-enabled-${t.slug}`} /> aktivan</label>
          </li>))}</ul>
      </Panel>
      <Panel title="Parametri i izvori" className="mt-6" testId="tools-admin-params">
        <div className="overflow-x-auto"><table className="w-full"><thead><tr className="text-left text-xs uppercase text-muted-foreground"><th>Parametar</th><th>Vrijednost</th><th>Izvor (https)</th><th>Provjera</th></tr></thead>
          <tbody>{d.params.map((p) => <ParamRow key={p.key} p={p} edit={edit} setEdit={setEdit} canEdit={canEdit} />)}</tbody></table></div>
        {canEdit && <button onClick={() => save()} disabled={busy || !Object.keys(edit).length} className={`${btnPrimary} mt-4`} data-testid="tadmin-save">Sačuvaj parametre</button>}
      </Panel>
      </>}
    </div>
  );
}
